import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { StatusChip } from '@/components/ui/status-chip'
import { ALERT_SEVERITY_STATUS_MAP, type Notification } from '@/api/types'
import { useInvokeNotificationAction, useNotifications } from '@/api/queries'

/**
 * The in-portal notification center (the shell bell) — the delivery surface of
 * the notification/escalation spine. It shows the current principal's OWN
 * notifications (routed to them via the ownership graph as an alert escalated),
 * the unread page badge, and an ack that dismisses the item AND stops the
 * escalation. Acking flows through the action gateway as notification.ack — the
 * portal's single mutation choke point — never a second write path. Digest-lane
 * items are shown honestly as "digest-queued" (the digest delivery lane is a
 * later phase); they do not inflate the page badge.
 */
export function NotificationCenter() {
  const { data, isLoading, isError } = useNotifications()
  const invoke = useInvokeNotificationAction()

  const notifications = data?.notifications ?? []
  const unread = data?.unreadCount ?? 0

  function ack(n: Notification) {
    invoke.mutate({ actionId: 'notification.ack', payload: { notificationId: n.id, serviceKeys: [n.service] } })
  }

  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={label}
          className="relative flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[7px] border border-border text-[13px] hover:border-accent"
        >
          🔔
          {unread > 0 && (
            <span
              aria-hidden
              className="absolute -top-1.5 -right-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-critical px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[12.5px] font-bold">Notifications</span>
          <span className="text-[11px] text-ink-2">{unread} unread</span>
        </div>

        <div className="max-h-[380px] overflow-y-auto">
          {isLoading && <div className="px-3 py-6 text-center text-[12px] text-ink-2">Loading…</div>}
          {isError && (
            <div className="px-3 py-6 text-center text-[12px] text-critical">Could not load notifications.</div>
          )}
          {!isLoading && !isError && notifications.length === 0 && (
            <div className="px-3 py-8 text-center text-[12px] text-ink-2">
              You&apos;re all caught up — no notifications.
            </div>
          )}
          {notifications.map((n) => (
            <NotificationRow key={n.id} n={n} onAck={ack} acking={invoke.isPending} />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function NotificationRow({ n, onAck, acking }: { n: Notification; onAck: (n: Notification) => void; acking: boolean }) {
  const digestQueued = n.lane === 'digest'
  return (
    <div className={`flex flex-col gap-1.5 border-b border-border px-3 py-2.5 last:border-b-0 ${n.read ? 'opacity-55' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <StatusChip status={ALERT_SEVERITY_STATUS_MAP[n.severity]}>{n.severity}</StatusChip>
            {digestQueued && (
              <span className="rounded-[5px] bg-unknown-weak px-1.5 py-0.5 text-[10px] font-semibold text-unknown">
                digest-queued
              </span>
            )}
          </div>
          <span className="truncate text-[12.5px] text-ink" title={n.title}>
            {n.title}
          </span>
          <span className="text-[11px] text-ink-2">
            {n.service}
            {n.teamKey ? ` · ${n.teamKey}` : ''} · L{n.level}
          </span>
        </div>
        {!n.read && n.lane === 'page' && (
          <button
            onClick={() => onAck(n)}
            disabled={acking}
            className="shrink-0 cursor-pointer rounded-[6px] border border-border bg-canvas px-2 py-1 text-[11px] font-semibold text-ink hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ack
          </button>
        )}
      </div>
    </div>
  )
}
